import { useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import useAuth from "../../hooks/useAuth";

type MenuLinkProps = {
  path: string;
  label: string;
  icon: React.ReactNode;
  end?: boolean;
  /** Paths (sub-routes of `path`) that have their own menu entry and must not keep this link active. */
  excludePaths?: string[];
}

const normalizePath = (path: string) =>
  path.length > 1 ? path.replace(/\/+$/, "") : path;

const MenuLink = ({path, icon, label, end, excludePaths}: MenuLinkProps) => {
  const classBase = useRef("py-2 md:py-3.5 px-3 md:px-5 max-w-fit lg:max-w-full flex gap-3 items-center rounded-md text-sm whitespace-nowrap");
  const {user} = useAuth();
  const navigateTo = useNavigate();
  const { pathname } = useLocation();
  if (!user) {
    navigateTo('auth');
    throw new Error("Opération non allouée");
  }

  // NavLink's default (non-`end`) matching is prefix-based, so e.g.
  // "/dashboard/admin/objects" also matches "/dashboard/admin/objects/import-template".
  // Compute active state manually so such sub-routes can be excluded while
  // genuine sub-pages (add/edit) still keep the parent entry highlighted.
  const currentPath = normalizePath(pathname);
  const targetPath = normalizePath(path);
  const isExcluded = (excludePaths ?? []).some((excludedPath) => {
    const excluded = normalizePath(excludedPath);
    return currentPath === excluded || currentPath.startsWith(`${excluded}/`);
  });
  const isActive =
    !isExcluded &&
    (end
      ? currentPath === targetPath
      : currentPath === targetPath || currentPath.startsWith(`${targetPath}/`));

  return (
    <Link to={path}
      aria-current={isActive ? 'page' : undefined}
      className={`${classBase.current} ${isActive ? 'bg-white' : ''}`}
      style={{
        backgroundColor: isActive ? '#FFFFFF' : '#004697',
        color: isActive ? '#0066B1' : '#FFFFFF'
      }}>
      {icon}
      <span className="hidden lg:block">{label}</span>
    </Link>
  )
}

export default MenuLink;